import Stack from '@mui/material/Stack'
import Typography from '@mui/material/Typography'
import { DataGridPro, useGridApiRef } from '@mui/x-data-grid-pro'
import { skipToken } from '@reduxjs/toolkit/query'
import React, { useCallback, useEffect, useMemo, useState } from 'react'

import {
  useLazyGetWOVersionsQuery,
  useLazyGetDisposalTicketVersionsByWoIdQuery,
  useLazyGetExceptionVersionsByWoIdQuery,
  useLazyGetContainerVersionsByWoIdQuery,
  useLazyGetWONoteVersionsQuery,
  useLazyGetFeeTaxLineVersionsByWoIdQuery,
  useLazyGetEventConfiguredServiceVersionsQuery,
} from 'api/entity-version/entityVersionCrud'
import { useGetWorkOrderContainersQuery } from 'api/inventory/workOrderContainer'
import { useLazyGetWorkOrderRouteActivityQuery } from 'api/work-order/getWorkOrderRouteActivity'
import { useLazyGetWorkOrderStatusHistoryQuery } from 'api/work-order/getWorkOrderStatusHistory'
import ActivityCell from 'components/common/activity/ActivityCell'
import ActivityHistoryActions from 'components/common/activity/ActivityHistoryActions'
import HHAccordion from 'components/common/HHAccordion'
import { ActivityContext } from 'components/configured-service/accordions/ActivityAccordion/ActivityProvider'
import useFilterHandlers from 'components/filter/useFilterHandlers'
import usePersistedFilterModel from 'components/filter/usePersistedFilterModel'
import { EXCEPTION_TYPES_NEW } from 'settings/constants/exceptions'
import { ROUTE_ACTIVITY_EVENT_TYPE } from 'settings/constants/routeActivity'
import { CONFIGURED_SERVICE_EVENT_TYPE } from 'settings/constants/service'
import { formatInvoiceStatus } from 'utils/activity/activityFormatters'
import ActivityTreeBuilder, { formatDateTime, type ActivityRow, type CustomBuilderFn } from 'utils/activity/ActivityTreeBuilder'
import { completionStampLabel } from 'utils/activity/completionStampLabel'
import disposalTicketBuilder from 'utils/activity/customBuilders/disposalTicketBuilder'
import exceptionBuilder from 'utils/activity/customBuilders/exceptionBuilder'
import feeTaxLineBuilder from 'utils/activity/customBuilders/feeTaxLineBuilder'
import routeActivityBuilder from 'utils/activity/customBuilders/routeActivityBuilder'
import statusEventBuilder from 'utils/activity/customBuilders/statusEventBuilder'
import workOrderContainerLinkBuilder from 'utils/activity/customBuilders/workOrderContainerLinkBuilder'
import workOrderNotesBuilder from 'utils/activity/customBuilders/workOrderNotesBuilder'
import { formatCentsToCurrency } from 'utils/price'
import { titleCaseFromEnum } from 'utils/string'

import type { GridColDef } from '@mui/x-data-grid-pro'

const { ONE_OFF } = CONFIGURED_SERVICE_EVENT_TYPE

type FeeTaxLineValue = { reason?: string; quantity?: number; feeChargeCents?: number }

type DisposalTicketValue = {
  materialName?: string
  description?: string
  quantity?: number
  unitOfMeasure?: string
  unitPriceCents?: number
}

type WorkOrderActivityProps = {
  workOrderId?: string
  configuredServiceId?: string
  configuredServiceEventId?: string
  configuredServiceEventType?: string
  filterModelStorageKey: string
  maxHeight?: number
  title?: string
}

const formatExceptionType = (value: unknown) => {
  if (!value) return value
  const formatted = titleCaseFromEnum(String(value))
  return EXCEPTION_TYPES_NEW[formatted as keyof typeof EXCEPTION_TYPES_NEW] || formatted
}

const formatActionType = (value: unknown) => {
  if (!value) return value
  return titleCaseFromEnum(String(value))
}

const formatFeeTaxLine = (value: FeeTaxLineValue | null | undefined) => {
  if (!value) return ''

  const reason = value.reason || ''
  const quantity = value.quantity || 0
  const feeChargeCents = value.feeChargeCents || 0

  const cost = formatCentsToCurrency(feeChargeCents)
  const price = formatCentsToCurrency(quantity * feeChargeCents)

  return (
    <Typography color="textPrimary" component="span">
      &apos;
      <Typography component="span" fontWeight={600}>
        {reason}
      </Typography>
      {` (${quantity} qty) x ${cost} = ${price}'`}
    </Typography>
  )
}

const formatDisposalTicketMaterial = (value: DisposalTicketValue | null | undefined) => {
  if (!value) return ''

  const name = value.materialName || value.description || ''
  const quantity = value.quantity || 0
  const unitOfMeasure = value.unitOfMeasure || ''
  const unitPriceCents = value.unitPriceCents || 0

  const cost = formatCentsToCurrency(unitPriceCents)
  const total = formatCentsToCurrency(quantity * unitPriceCents)

  return (
    <Typography color="textPrimary" component="span">
      &apos;
      {name && (
        <Typography component="span" fontWeight={600}>
          {`${name} `}
        </Typography>
      )}
      {`${cost} / ${unitOfMeasure} x${quantity} =${total}`}
    </Typography>
  )
}

const WorkOrderActivity = ({
  workOrderId,
  configuredServiceId,
  configuredServiceEventId,
  configuredServiceEventType,
  filterModelStorageKey,
  maxHeight = 600,
  title = '',
}: WorkOrderActivityProps) => {
  const apiRef = useGridApiRef()
  const [getWOVersions, { data: woVersionsData, isFetching: isFetchingWOVersions }] = useLazyGetWOVersionsQuery()
  const [getDisposalTicketVersionByWoId, { data: disposalTicketVersions, isFetching: isFetchingDisposalTicketVersions }] =
    useLazyGetDisposalTicketVersionsByWoIdQuery()
  const [getExceptionVersionByWoId, { data: exceptionVersionsData, isFetching: isFetchingExceptionVersions }] =
    useLazyGetExceptionVersionsByWoIdQuery()
  const [getContainerVersionsByWoId, { data: containerVersionsData, isFetching: isFetchingContainerVersions }] =
    useLazyGetContainerVersionsByWoIdQuery()
  // Subscribed, not lazy, to share the containers section's cache entry; a lazy trigger forces a second request for the same work order.
  const { data: workOrderContainersData } = useGetWorkOrderContainersQuery(workOrderId ? { workOrderId } : skipToken)
  const [getWoNoteVersions, { data: woNoteVersionsData, isFetching: isFetchingWoNoteVersions }] = useLazyGetWONoteVersionsQuery()
  const [getFeeTaxLineVersionsByWoId, { data: feeTaxLineVersionsData, isFetching: isFetchingFeeTaxLineVersions }] =
    useLazyGetFeeTaxLineVersionsByWoIdQuery()
  const [getCSEventVersions, { data: csEventVersionsData, isFetching: isFetchingCsEventVersions }] =
    useLazyGetEventConfiguredServiceVersionsQuery()
  const [getRouteActivity, { data: routeActivityData, isFetching: isFetchingRouteActivity }] = useLazyGetWorkOrderRouteActivityQuery()
  const [getStatusHistory, { data: statusHistoryData, isFetching: isFetchingStatusHistory }] = useLazyGetWorkOrderStatusHistoryQuery()
  // (x ?? '') not String(x): lodash toLower turned undefined into '', String turns it into 'undefined'.
  const isOneOffEvent = useMemo(() => (configuredServiceEventType ?? '').toLowerCase() === ONE_OFF, [configuredServiceEventType])

  const [showSequenceChanges, setShowSequenceChanges] = useState(true)
  const handleToggleSequenceChanges = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => setShowSequenceChanges(Boolean(event?.target?.checked)),
    []
  )
  const hasSequenceChanges = useMemo(
    () => Array.isArray(routeActivityData) && routeActivityData.some(row => row?.eventType === ROUTE_ACTIVITY_EVENT_TYPE.SEQUENCE),
    [routeActivityData]
  )

  const formatterRegistry = useMemo(
    () => ({
      exceptionType: formatExceptionType,
      actionType: formatActionType,
      invoiceStatus: formatInvoiceStatus,
      generalFee: formatFeeTaxLine,
      disposalTicketMaterial: formatDisposalTicketMaterial,
      servicedAt: formatDateTime,
    }),
    []
  )

  const activityContextValue = useMemo(
    () => ({
      formatterRegistry,
    }),
    [formatterRegistry]
  )

  const activityRows = useMemo(() => {
    const builder = new ActivityTreeBuilder()
    builder.registerCustomBuilder('exceptions', exceptionBuilder as CustomBuilderFn)
    builder.registerCustomBuilder('workOrderContainers', workOrderContainerLinkBuilder as CustomBuilderFn)
    builder.registerCustomBuilder('workOrderNotes', workOrderNotesBuilder as CustomBuilderFn)
    builder.registerCustomBuilder('feeTaxLines', feeTaxLineBuilder as CustomBuilderFn)
    builder.registerCustomBuilder('disposalTickets', disposalTicketBuilder as CustomBuilderFn)
    builder.registerCustomBuilder('routeActivity', routeActivityBuilder)
    builder.registerCustomBuilder('statusEvents', statusEventBuilder)

    const versions = Array.isArray(woVersionsData?.versions) ? woVersionsData?.versions : []
    if (versions.length > 0) {
      builder.addChanges(versions, {
        entityName: 'Work Order',
        fieldConfig: {
          serviceDate: 'Service Date',
          workOrderStatus: 'Status',
          invoiceStatus: 'Invoice Status',
          purchaseOrderNumber: 'Purchase Order Number',
          servicedAt: completionStampLabel,
          driverUsername: 'Driver',
        },
      })
    }
    if (csEventVersionsData?.versions) {
      builder.addChanges(csEventVersionsData.versions, {
        entityName: 'Work Order',
        fieldConfig: {
          actionType: 'Action Type',
        },
      })
    }
    if (exceptionVersionsData?.exceptions) {
      builder.runCustomBuilder('exceptions', { exceptionsById: exceptionVersionsData.exceptions })
    }
    if (containerVersionsData?.containers) {
      builder.runCustomBuilder('workOrderContainers', {
        containersById: containerVersionsData.containers,
        workOrderContainers: workOrderContainersData?.containers || [],
      })
    }
    if (woNoteVersionsData?.notes) {
      builder.runCustomBuilder('workOrderNotes', {
        workOrderNotesById: woNoteVersionsData?.notes,
      })
    }
    if (feeTaxLineVersionsData?.feeTaxLines) {
      builder.runCustomBuilder('feeTaxLines', { feeTaxLinesById: feeTaxLineVersionsData.feeTaxLines })
    }
    if (disposalTicketVersions?.disposalTickets) {
      builder.runCustomBuilder('disposalTickets', { disposalTickets: disposalTicketVersions.disposalTickets })
    }
    if (Array.isArray(routeActivityData) && routeActivityData.length > 0) {
      builder.runCustomBuilder('routeActivity', { rows: routeActivityData, includeSequenceChanges: showSequenceChanges })
    }
    if (statusHistoryData?.statusEvents) {
      builder.runCustomBuilder('statusEvents', {
        statusEvents: statusHistoryData.statusEvents,
        workOrderContainers: workOrderContainersData?.containers || [],
      })
    }

    return builder.buildTree()
  }, [
    woVersionsData,
    exceptionVersionsData,
    containerVersionsData,
    workOrderContainersData,
    woNoteVersionsData,
    feeTaxLineVersionsData,
    disposalTicketVersions,
    csEventVersionsData,
    routeActivityData,
    statusHistoryData,
    showSequenceChanges,
  ])

  const isLoading = useMemo(
    () =>
      Boolean(
        isFetchingWOVersions ||
          isFetchingDisposalTicketVersions ||
          isFetchingExceptionVersions ||
          isFetchingContainerVersions ||
          isFetchingWoNoteVersions ||
          isFetchingFeeTaxLineVersions ||
          isFetchingCsEventVersions ||
          isFetchingRouteActivity ||
          isFetchingStatusHistory
      ),
    [
      isFetchingWOVersions,
      isFetchingDisposalTicketVersions,
      isFetchingExceptionVersions,
      isFetchingContainerVersions,
      isFetchingWoNoteVersions,
      isFetchingFeeTaxLineVersions,
      isFetchingCsEventVersions,
      isFetchingRouteActivity,
      isFetchingStatusHistory,
    ]
  )

  const [filterModel, setFilterModel] = usePersistedFilterModel(filterModelStorageKey)
  const { handleFilterModelChange } = useFilterHandlers(filterModel, setFilterModel, true)

  const columns = useMemo<GridColDef<ActivityRow>[]>(
    () => [
      {
        field: 'id',
        flex: 1,
        renderCell: ActivityCell,
        disableExport: true,
      },
      { field: 'field', type: 'string', filterable: true },
      { field: 'modifiedBy', type: 'string', filterable: true },
      { field: 'modifiedAt', type: 'string', filterable: true },
      { field: 'oldValue', type: 'string', filterable: true },
      { field: 'newValue', type: 'string', filterable: true },
      { field: 'changeType', type: 'string' },
    ],
    []
  )

  const getRowHeight = useCallback(() => 'auto', [])
  const [filterAnchorEl, setFilterAnchorEl] = useState<HTMLElement | null>(null)

  useEffect(() => {
    if (workOrderId) {
      getWOVersions({ woId: workOrderId })
      getDisposalTicketVersionByWoId({ woId: workOrderId })
      getExceptionVersionByWoId({ woId: workOrderId })
      getContainerVersionsByWoId({ woId: workOrderId })
      getWoNoteVersions({ woId: workOrderId })
      getFeeTaxLineVersionsByWoId({ woId: workOrderId })
      getRouteActivity({ workOrderId })
      getStatusHistory({ workOrderId })
    }
  }, [
    workOrderId,
    getWOVersions,
    getDisposalTicketVersionByWoId,
    getExceptionVersionByWoId,
    getContainerVersionsByWoId,
    getWoNoteVersions,
    getFeeTaxLineVersionsByWoId,
    getRouteActivity,
    getStatusHistory,
  ])

  useEffect(() => {
    if (configuredServiceId && configuredServiceEventId && isOneOffEvent) {
      getCSEventVersions({ csId: configuredServiceId, csEventId: configuredServiceEventId })
    }
  }, [configuredServiceId, configuredServiceEventId, isOneOffEvent, getCSEventVersions])

  const actions = (
    <ActivityHistoryActions
      apiRef={apiRef}
      sequenceChangesShown={showSequenceChanges}
      showSequenceChangesToggle={hasSequenceChanges}
      onFilterAnchorChange={setFilterAnchorEl}
      onToggleSequenceChanges={handleToggleSequenceChanges}
    />
  )

  const grid = (
    <ActivityContext.Provider value={activityContextValue}>
      <Stack spacing={1}>
        <DataGridPro
          disableColumnMenu
          disableColumnResize
          disableColumnSelector
          disableDensitySelector
          disableRowSelectionOnClick
          apiRef={apiRef}
          columnHeaderHeight={0}
          columns={columns}
          columnVisibilityModel={{
            field: false,
            oldValue: false,
            newValue: false,
            modifiedBy: false,
            modifiedAt: false,
            changeType: false,
            rev: false,
          }}
          filterModel={filterModel}
          getRowHeight={getRowHeight}
          loading={isLoading}
          rows={activityRows}
          slotProps={{ panel: { anchorEl: filterAnchorEl } }}
          sx={{
            maxHeight,
            overflow: 'auto',
          }}
          onFilterModelChange={handleFilterModelChange}
        />
      </Stack>
    </ActivityContext.Provider>
  )

  if (!title) return grid

  return (
    <HHAccordion
      isExpandCollapseHeightSame
      isExpanded
      showCustomActions
      withNewColors
      customActionContent={actions}
      showActionIcon={false}
      summary={title}
    >
      {grid}
    </HHAccordion>
  )
}

export default WorkOrderActivity
